import React, { Children } from 'react';
import { Route, Routes } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowDownIcon, ArrowRightIcon, ArrowUpRightIcon, CalendarDaysIcon, CirclePlayIcon, Clock3Icon, MapPinIcon, MountainIcon, SparklesIcon } from 'lucide-react';
import { Footer } from './components/Footer';
import { Header } from './components/Header';
import { SectionHeading } from './components/SectionHeading';
import { EverestTreks } from './pages/EverestTreks';
import { Guides } from './pages/Guides';
import { HoneymoonTours } from './pages/HoneymoonTours';
import { ManasluCircuitTrek } from './pages/ManasluCircuitTrek';
import { AboutUs } from './pages/AboutUs';
import { AwardsAndAchievement } from './pages/AwardsAndAchievement';
import { CSR } from './pages/CSR';
import { ContactUs } from './pages/ContactUs';
import { CustomerReviews } from './pages/CustomerReviews';
import { ExploreOurTeam } from './pages/ExploreOurTeam';
import { LegalDocuments } from './pages/LegalDocuments';
import { NepalTravelBlog } from './pages/NepalTravelBlog';
import { PartnerWithUs } from './pages/PartnerWithUs';
import { TermsAndConditions } from './pages/TermsAndConditions';
import { VideoReviews } from './pages/VideoReviews';
import { WhyChooseUs } from './pages/WhyChooseUs';
const upcomingTreks = [{
  title: 'Manaslu Circuit Trek',
  date: '12 Oct 2026',
  duration: '14 days',
  elevation: '5,106m',
  spaces: '4 places left',
  price: 'From US$ 1,890',
  image: 'https://images.unsplash.com/photo-1544735716-392fe2489ffa?auto=format&fit=crop&w=1200&q=85',
  href: '/treks/manaslu-circuit'
}, {
  title: 'Tsum Valley Retreat',
  date: '26 Oct 2026',
  duration: '12 days',
  elevation: '3,700m',
  spaces: '6 places left',
  price: 'From US$ 1,640',
  image: 'https://images.unsplash.com/photo-1548013146-72479768bada?auto=format&fit=crop&w=1000&q=85',
  href: '#contact'
}, {
  title: 'Nar Phu Passage',
  date: '04 Nov 2026',
  duration: '13 days',
  elevation: '5,240m',
  spaces: '8 places left',
  price: 'From US$ 1,760',
  image: 'https://images.unsplash.com/photo-1500530855697-b586d89ba3ee?auto=format&fit=crop&w=1000&q=85',
  href: '#contact'
}];
const bestSellingTreks = [{
  title: 'Manaslu Circuit Trek',
  duration: '14–17 days',
  elevation: '5,106m',
  price: 'From US$ 1,890',
  image: 'https://images.unsplash.com/photo-1544735716-392fe2489ffa?auto=format&fit=crop&w=900&q=85',
  href: '/treks/manaslu-circuit'
}, {
  title: 'Tsum Valley Trek',
  duration: '12 days',
  elevation: '3,700m',
  price: 'From US$ 1,640',
  image: 'https://images.unsplash.com/photo-1548013146-72479768bada?auto=format&fit=crop&w=900&q=85',
  href: '#contact'
}, {
  title: 'Nar Phu Valley Trek',
  duration: '13 days',
  elevation: '5,240m',
  price: 'From US$ 1,760',
  image: 'https://images.unsplash.com/photo-1500530855697-b586d89ba3ee?auto=format&fit=crop&w=900&q=85',
  href: '#contact'
}, {
  title: 'Annapurna Sanctuary',
  duration: '11 days',
  elevation: '4,130m',
  price: 'From US$ 1,450',
  image: 'https://images.unsplash.com/photo-1519681393784-d120267933ba?auto=format&fit=crop&w=900&q=85',
  href: '#contact'
}, {
  title: 'Langtang Valley Trek',
  duration: '10 days',
  elevation: '4,984m',
  price: 'From US$ 1,280',
  image: 'https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?auto=format&fit=crop&w=900&q=85',
  href: '#contact'
}, {
  title: 'Everest View Trek',
  duration: '9 days',
  elevation: '3,860m',
  price: 'From US$ 1,350',
  image: 'https://images.unsplash.com/photo-1486911278844-a81c5267e227?auto=format&fit=crop&w=900&q=85',
  href: '#contact'
}];
const guides = [{
  category: 'Planning',
  date: '08 Feb 2026',
  readTime: '7 min read',
  title: 'The right season for the Manaslu Circuit',
  excerpt: 'A simple guide to clear skies, trail conditions, and the quieter weeks worth knowing about.',
  image: 'https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?auto=format&fit=crop&w=1000&q=85',
  href: '/guides/nepal-travel-guide'
}, {
  category: 'Mountain knowledge',
  date: '24 Jan 2026',
  readTime: '6 min read',
  title: 'How to acclimatise with patience',
  excerpt: 'Why the best altitude strategy is less about speed and more about a steady, listening pace.',
  image: 'https://images.unsplash.com/photo-1533130061792-64b345e4a833?auto=format&fit=crop&w=1000&q=85',
  href: '/guides/nepal-travel-guide'
}, {
  category: 'On the trail',
  date: '12 Jan 2026',
  readTime: '5 min read',
  title: 'What life in a teahouse really feels like',
  excerpt: 'Warm dal bhat, wool blankets, and the small rituals that make a trekking day complete.',
  image: 'https://images.unsplash.com/photo-1518002054494-3a6f94352e9d?auto=format&fit=crop&w=1000&q=85',
  href: '/guides/nepal-travel-guide'
}];
const reveal = {
  hidden: {
    opacity: 0,
    y: 22
  },
  visible: {
    opacity: 1,
    y: 0
  }
};
function HomePage() {
  return <div id="top" className="min-h-screen overflow-hidden bg-[#f8f5ed] text-[#17362e]" data-id="element-0">
      <Header data-id="element-1" />
      <main data-id="element-2">
        <section className="relative flex min-h-[750px] items-end bg-[#18372e] pb-16 pt-32 text-white sm:min-h-screen sm:pb-24" data-id="element-3">
          <video className="absolute inset-0 h-full w-full object-cover opacity-80" autoPlay muted loop playsInline poster="https://images.unsplash.com/photo-1486911278844-a81c5267e227?auto=format&fit=crop&w=2200&q=90" aria-label="A cinematic Himalayan mountain landscape" data-id="element-4">
            <source src="https://videos.pexels.com/video-files/3129595/3129595-hd_1920_1080_25fps.mp4" type="video/mp4" data-id="element-5" />
          </video>
          <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(8,27,22,0.74),rgba(8,27,22,0.14)_70%),linear-gradient(0deg,rgba(8,27,22,0.72),transparent_55%)]" data-id="element-6" />
          <motion.div initial="hidden" animate="visible" transition={{
          staggerChildren: 0.14,
          delayChildren: 0.2
        }} className="relative mx-auto w-full max-w-[1440px] px-5 sm:px-8 lg:px-12" data-id="element-7">
            <motion.p variants={reveal} className="mb-5 text-[10px] font-bold uppercase tracking-[0.3em] text-[#efc778]" data-id="element-8">
              Nepal · Himalaya · Since 2008
            </motion.p>
            <motion.h1 variants={reveal} className="max-w-4xl font-serif text-5xl leading-[0.94] sm:text-7xl lg:text-[88px]" data-id="element-9">
              The mountain
              <br data-id="element-10" />
              <i className="font-normal" data-id="element-11">calls you home.</i>
            </motion.h1>
            <motion.p variants={reveal} className="mt-7 max-w-lg text-base leading-7 text-white/85" data-id="element-12">
              Nepal trek and tour packages shaped around high trails, living
              cultures, and the quiet moments between the ridgelines — led by
              people who know these mountains by name.
            </motion.p>
            <motion.div variants={reveal} className="mt-9 flex flex-wrap gap-3" data-id="element-13">
              <a href="#treks" className="bg-[#d8a75a] px-6 py-4 text-[11px] font-bold uppercase tracking-[0.16em] text-[#17362e] transition hover:bg-[#efc778]" data-id="element-14">
                Explore treks
              </a>
              <a href="#about" className="border border-white/50 px-6 py-4 text-[11px] font-bold uppercase tracking-[0.16em] text-white transition hover:border-white hover:bg-white/10" data-id="element-15">
                Our story
              </a>
            </motion.div>
          </motion.div>
          <a href="#about" className="absolute bottom-7 right-5 flex items-center gap-3 text-[10px] font-bold uppercase tracking-[0.18em] sm:right-8 lg:right-12" aria-label="Scroll to introduction" data-id="element-16">
            Scroll to discover <ArrowDownIcon className="h-4 w-4" data-id="element-17" />
          </a>
        </section>

        <section id="about" className="bg-[#f8f5ed] py-20 sm:py-28" data-id="element-18">
          <div className="mx-auto grid max-w-[1180px] gap-12 px-5 sm:px-8 lg:grid-cols-[0.8fr_1.2fr] lg:gap-24" data-id="element-19">
            <div data-id="element-20">
              <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#a66e2e]" data-id="element-21">
                Welcome to Manaslu
              </p>
              <div className="mt-6 h-px w-16 bg-[#a66e2e]" data-id="element-22" />
            </div>
            <div data-id="element-23">
              <h2 className="font-serif text-4xl leading-[1.12] text-[#17362e] sm:text-5xl" data-id="element-24">
                There is more than one way to reach a summit.
              </h2>
              <p className="mt-7 max-w-2xl text-lg leading-8 text-[#52635a]" data-id="element-25">
                We believe the best journeys are measured in more than miles.
                They are found in the steam of a tea house, the generosity of a
                village welcome, and the quiet rhythm of a trail shared with
                good people.
              </p>
              <a href="#guide" className="mt-8 inline-flex items-center gap-2 border-b border-[#a66e2e] pb-2 text-[11px] font-bold uppercase tracking-[0.16em] text-[#17362e]" data-id="element-26">
                Meet our mountain family <ArrowRightIcon className="h-4 w-4" data-id="element-27" />
              </a>
            </div>
          </div>
        </section>

        <section id="treks" className="bg-[#e7e7db] py-20 sm:py-28" data-id="element-28">
          <div className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12" data-id="element-29">
            <div className="flex flex-col justify-between gap-8 md:flex-row md:items-end" data-id="element-30">
              <SectionHeading eyebrow="Guided small-group departures" title="Upcoming journeys." description="A limited number of considered departures each season, led by people who know these trails by heart." data-id="element-31" />
              <a href="#contact" className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.16em] text-[#17362e]" data-id="element-32">
                Ask about a private trek <ArrowRightIcon className="h-4 w-4" data-id="element-33" />
              </a>
            </div>

            <div className="mt-12 overflow-hidden border border-[#aac6a8] bg-[#e5f0e1] p-3 sm:p-5" data-id="element-34">
              <div className="grid gap-3 lg:grid-cols-3" data-id="element-35">
                {upcomingTreks.map((trek, index) => <article key={trek.title} className="group relative min-h-[420px] overflow-hidden bg-[#17362e] text-white sm:min-h-[480px]" data-id="element-36">
                    <img src={trek.image} alt={`${trek.title} landscape`} className="absolute inset-0 h-full w-full object-cover opacity-75 transition duration-700 group-hover:scale-105" data-id="element-37" />
                    <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(10,32,25,0.08)_25%,rgba(10,32,25,0.94)_100%)]" data-id="element-38" />
                    <div className="relative flex h-full min-h-[420px] flex-col justify-end p-6 sm:min-h-[480px] sm:p-7" data-id="element-39">
                      <div data-id="element-40">
                        <h3 className="max-w-xs font-serif text-3xl leading-tight sm:text-4xl" data-id="element-41">
                          {trek.title}
                        </h3>
                        <div className="mt-6 flex flex-wrap gap-x-4 gap-y-2 border-y border-white/30 py-4 text-[10px] font-bold uppercase tracking-[0.13em] text-white/80" data-id="element-42">
                          <span className="flex items-center gap-1.5" data-id="element-43">
                            <Clock3Icon className="h-3.5 w-3.5 text-[#c8dfbd]" data-id="element-44" />{' '}
                            {trek.duration}
                          </span>
                          <span className="flex items-center gap-1.5" data-id="element-45">
                            <MountainIcon className="h-3.5 w-3.5 text-[#c8dfbd]" data-id="element-46" />{' '}
                            {trek.elevation}
                          </span>
                        </div>
                        <div className="mt-5 flex items-end justify-between gap-4" data-id="element-47">
                          <div data-id="element-48">
                            <p className="text-[9px] font-bold uppercase tracking-[0.16em] text-white/60" data-id="element-49">
                              From
                            </p>
                            <p className="mt-1 font-serif text-2xl" data-id="element-50">
                              {trek.price.replace('From ', '')}
                            </p>
                          </div>
                          <a href={trek.href} aria-label={`View ${trek.title}`} className="flex h-11 w-11 items-center justify-center border border-[#c8dfbd] text-[#c8dfbd] transition hover:bg-[#c8dfbd] hover:text-[#17362e]" data-id="element-51">
                            <ArrowUpRightIcon className="h-5 w-5" data-id="element-52" />
                          </a>
                        </div>
                      </div>
                    </div>
                  </article>)}
              </div>
            </div>
          </div>

          <div className="mx-auto mt-20 max-w-[1440px] px-5 sm:mt-28 sm:px-8 lg:px-12" data-id="element-53">
            <div className="relative overflow-hidden bg-[#315c3b] px-7 py-14 text-white sm:px-12 sm:py-20" data-id="element-54">
              <MountainIcon className="absolute right-8 top-8 h-24 w-24 text-white/10 sm:right-16 sm:top-12 sm:h-40 sm:w-40" strokeWidth={0.8} data-id="element-55" />
              <div className="relative max-w-3xl" data-id="element-56">
                <p className="text-[10px] font-bold uppercase tracking-[0.27em] text-[#d8ebcd]" data-id="element-57">
                  In the Himalaya, time is the greatest luxury
                </p>
                <p className="mt-5 font-serif text-4xl leading-tight sm:text-6xl" data-id="element-58">
                  “Come for the mountains. Leave with a different way of
                  seeing.”
                </p>
                <a href="#contact" className="mt-8 inline-flex items-center gap-2 border-b border-[#d8ebcd] pb-2 text-[10px] font-bold uppercase tracking-[0.16em] text-[#d8ebcd]" data-id="element-59">
                  Find your journey <ArrowRightIcon className="h-4 w-4" data-id="element-60" />
                </a>
              </div>
            </div>
          </div>

          <div className="mx-auto mt-20 max-w-[1440px] px-5 sm:mt-28 sm:px-8 lg:px-12" data-id="element-61">
            <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end" data-id="element-62">
              <SectionHeading eyebrow="Traveller favourites" title="Best Selling Trips." description="Six enduring routes, each offering its own pace, landscape, and reason to keep walking." data-id="element-63" />
              <a href="#contact" className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.16em] text-[#17362e]" data-id="element-64">
                View all treks <ArrowRightIcon className="h-4 w-4" data-id="element-65" />
              </a>
            </div>
            <div className="mt-12 grid gap-x-5 gap-y-10 sm:grid-cols-2 lg:grid-cols-3" data-id="element-66">
              {bestSellingTreks.map(trek => <article key={trek.title} className="group border-b border-[#bdc4b7] pb-6" data-id="element-67">
                  <a href={trek.href} className="block overflow-hidden" aria-label={`Explore ${trek.title}`} data-id="element-68">
                    <img src={trek.image} alt={`${trek.title} in the Himalaya`} className="h-56 w-full object-cover transition duration-700 group-hover:scale-105" data-id="element-69" />
                  </a>
                  <div className="mt-5 flex items-start justify-between gap-4" data-id="element-70">
                    <h3 className="font-serif text-2xl leading-tight text-[#17362e]" data-id="element-71">
                      {trek.title}
                    </h3>
                    <a href={trek.href} aria-label={`View ${trek.title}`} className="flex h-9 w-9 shrink-0 items-center justify-center border border-[#8b998e] transition hover:border-[#17362e] hover:bg-[#17362e] hover:text-white" data-id="element-72">
                      <ArrowUpRightIcon className="h-4 w-4" data-id="element-73" />
                    </a>
                  </div>
                  <div className="mt-4 flex flex-wrap items-center justify-between gap-3 text-[10px] font-bold uppercase tracking-[0.13em] text-[#66766c]" data-id="element-74">
                    <div className="flex gap-4" data-id="element-75">
                      <span className="flex items-center gap-1.5" data-id="element-76">
                        <Clock3Icon className="h-3.5 w-3.5 text-[#4f8057]" data-id="element-77" />{' '}
                        {trek.duration}
                      </span>
                      <span className="flex items-center gap-1.5" data-id="element-78">
                        <MapPinIcon className="h-3.5 w-3.5 text-[#4f8057]" data-id="element-79" />{' '}
                        {trek.elevation}
                      </span>
                    </div>
                    <span className="font-serif text-base normal-case tracking-normal text-[#254c30]" data-id="element-80">
                      {trek.price}
                    </span>
                  </div>
                </article>)}
            </div>
          </div>
        </section>

        <section id="videos" className="bg-[#17362e] py-20 text-white sm:py-28" data-id="element-81">
          <div className="mx-auto max-w-[1180px] px-5 sm:px-8 lg:px-12" data-id="element-82">
            <div className="flex flex-col justify-between gap-5 sm:flex-row sm:items-end" data-id="element-83">
              <SectionHeading eyebrow="Latest film" title="Through the Larkya La." description="A field film from the high pass, where the long walk reveals its reward." data-id="element-84" />
              <a href="#contact" className="shrink-0 text-[10px] font-bold uppercase tracking-[0.16em] text-[#efc778]" data-id="element-85">
                Visit our YouTube{' '}
                <ArrowUpRightIcon className="ml-1 inline h-4 w-4" data-id="element-86" />
              </a>
            </div>
            <article className="group mt-12 overflow-hidden bg-[#10261f]" data-id="element-87">
              <a href="#contact" className="relative block aspect-video overflow-hidden" aria-label="Play Through the Larkya La on YouTube" data-id="element-88">
                <img src="https://images.unsplash.com/photo-1528181304800-259b08848526?auto=format&fit=crop&w=1800&q=85" alt="Mountain trail beneath snow-covered Himalayan peaks" className="h-full w-full object-cover transition duration-700 group-hover:scale-105" data-id="element-89" />
                <span className="absolute inset-0 bg-[#10261f]/25" data-id="element-90" />
                <span className="absolute inset-0 flex items-center justify-center" data-id="element-91">
                  <span className="flex h-16 w-16 items-center justify-center rounded-full bg-[#c92727] text-white shadow-xl transition group-hover:scale-110 sm:h-20 sm:w-20" data-id="element-92">
                    <CirclePlayIcon className="ml-0.5 h-8 w-8 fill-white sm:h-10 sm:w-10" data-id="element-93" />
                  </span>
                </span>
                <span className="absolute bottom-5 left-5 bg-black/75 px-3 py-1.5 text-[10px] font-bold tracking-[0.16em] text-white" data-id="element-94">
                  12:48
                </span>
              </a>
              <div className="flex flex-col justify-between gap-5 p-6 sm:flex-row sm:items-center sm:p-8" data-id="element-95">
                <div data-id="element-96">
                  <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#efc778]" data-id="element-97">
                    Manaslu field film · Episode 04
                  </p>
                  <h3 className="mt-2 font-serif text-3xl" data-id="element-98">
                    A crossing above the clouds
                  </h3>
                </div>
                <a href="#contact" className="inline-flex shrink-0 items-center gap-2 border border-white/40 px-5 py-3 text-[10px] font-bold uppercase tracking-[0.16em] transition hover:border-[#efc778] hover:text-[#efc778]" data-id="element-99">
                  Watch on YouTube <ArrowUpRightIcon className="h-4 w-4" data-id="element-100" />
                </a>
              </div>
            </article>
          </div>
        </section>

        <section id="guide" className="bg-[#f8f5ed] py-20 sm:py-28" data-id="element-101">
          <div className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12" data-id="element-102">
            <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end" data-id="element-103">
              <SectionHeading eyebrow="Travel journal" title="Guides for the way there." description="Useful, honest reads from our guides and the Himalayan trail." data-id="element-104" />
              <a href="#contact" className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.16em] text-[#17362e]" data-id="element-105">
                All journal entries <ArrowRightIcon className="h-4 w-4" data-id="element-106" />
              </a>
            </div>
            <div className="mt-12 grid gap-7 md:grid-cols-2 lg:grid-cols-3" data-id="element-107">
              {guides.map(guide => <article key={guide.title} className="group flex flex-col border-b border-[#cbd0c2] pb-7" data-id="element-108">
                  <a href={guide.href} className="block overflow-hidden" aria-label={`Read article: ${guide.title}`} data-id="element-109">
                    <img src={guide.image} alt="" className="h-60 w-full object-cover transition duration-700 group-hover:scale-105" data-id="element-110" />
                  </a>
                  <div className="mt-5 flex items-center gap-3 text-[9px] font-bold uppercase tracking-[0.15em] text-[#a66e2e]" data-id="element-111">
                    <span data-id="element-112">{guide.category}</span>
                    <span className="h-1 w-1 rounded-full bg-[#a66e2e]" data-id="element-113" />
                    <span className="text-[#718074]" data-id="element-114">
                      {guide.date} · {guide.readTime}
                    </span>
                  </div>
                  <h3 className="mt-3 font-serif text-2xl leading-snug text-[#17362e]" data-id="element-115">
                    {guide.title}
                  </h3>
                  <p className="mt-3 text-sm leading-6 text-[#5d6e63]" data-id="element-116">
                    {guide.excerpt}
                  </p>
                  <a href={guide.href} className="mt-5 inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.16em] text-[#17362e]" data-id="element-117">
                    Read article{' '}
                    <ArrowRightIcon className="h-4 w-4 transition-transform group-hover:translate-x-1" data-id="element-118" />
                  </a>
                </article>)}
            </div>
          </div>
        </section>

        <section id="stories" className="bg-[#c97b37] px-5 py-16 text-[#17362e] sm:px-8 lg:px-12" data-id="element-119">
          <div className="mx-auto flex max-w-[1100px] flex-col items-start justify-between gap-8 md:flex-row md:items-center" data-id="element-120">
            <div className="flex items-start gap-4" data-id="element-121">
              <SparklesIcon className="mt-1 h-6 w-6" data-id="element-122" />
              <div data-id="element-123">
                <p className="text-[10px] font-bold uppercase tracking-[0.2em]" data-id="element-124">
                  Your next chapter starts here
                </p>
                <h2 className="mt-3 font-serif text-3xl sm:text-4xl" data-id="element-125">
                  Tell us how you want to travel.
                </h2>
              </div>
            </div>
            <a href="#contact" className="shrink-0 bg-[#17362e] px-6 py-4 text-[11px] font-bold uppercase tracking-[0.16em] text-white transition hover:bg-[#10261f]" data-id="element-126">
              Plan a trip
            </a>
          </div>
        </section>
      </main>
      <Footer data-id="element-127" />
    </div>;
}
export function App() {
  return <Routes data-id="element-128">
      <Route path="/" element={<HomePage data-id="element-130" />} data-id="element-129" />
      <Route path="/guides" element={<Guides data-id="element-132" />} data-id="element-131" />
      <Route path="/guides/nepal-travel-guide" element={<NepalTravelBlog data-id="element-134" />} data-id="element-133" />
      <Route path="/about" element={<AboutUs data-id="element-136" />} data-id="element-135" />
      <Route path="/company/team" element={<ExploreOurTeam data-id="element-138" />} data-id="element-137" />
      <Route path="/company/why-choose-us" element={<WhyChooseUs data-id="element-140" />} data-id="element-139" />
      <Route path="/company/csr" element={<CSR data-id="element-142" />} data-id="element-141" />
      <Route path="/company/legal-documents" element={<LegalDocuments data-id="element-144" />} data-id="element-143" />
      <Route path="/company/terms-and-conditions" element={<TermsAndConditions data-id="element-146" />} data-id="element-145" />
      <Route path="/company/awards" element={<AwardsAndAchievement data-id="element-148" />} data-id="element-147" />
      <Route path="/company/customer-reviews" element={<CustomerReviews data-id="element-150" />} data-id="element-149" />
      <Route path="/company/video-reviews" element={<VideoReviews data-id="element-152" />} data-id="element-151" />
      <Route path="/company/partner-with-us" element={<PartnerWithUs data-id="element-154" />} data-id="element-153" />
      <Route path="/contact" element={<ContactUs data-id="element-156" />} data-id="element-155" />
      <Route path="/tours/honeymoon" element={<HoneymoonTours data-id="element-158" />} data-id="element-157" />
      <Route path="/treks/everest" element={<EverestTreks data-id="element-160" />} data-id="element-159" />
      <Route path="/treks/manaslu-circuit" element={<ManasluCircuitTrek data-id="element-162" />} data-id="element-161" />
      <Route path="*" element={<HomePage data-id="element-164" />} data-id="element-163" />
    </Routes>;
}